import { ValueScope } from 'enums/valueScope';
import * as Calculation from 'types/Recipe/Calculation';
import * as ErpRecipe from 'types/Recipe/Detail/ErpNew/ErpRecipe';
import { ID } from 'types/Recipe/Detail/ErpNew/ErpRecipe';
import * as ErpRecipeList from 'types/Recipe/Detail/ErpNew/ErpRecipeList';
import * as IngredientList from 'types/Recipe/Detail/ErpNew/IngredientList';
import * as Product from 'types/Recipe/Detail/ErpNew/Product';

export type Type = {
	erpRecipeId: string | null;
	erpRecipes: ErpRecipeList.Type;
	selectedColumns: string[];
	valueScope: ValueScope;
	comparisonRight: { [ingredientId: string]: string | null };
	comparisonLeft: { [ingredientId: string]: string | null };
	searchTerm: string;
	recalculationSignal: number;
};

export function create(): Type {
	return {
		erpRecipeId: null,
		erpRecipes: ErpRecipeList.create(),
		selectedColumns: [],
		valueScope: ValueScope.HundredGrams,
		comparisonRight: {},
		comparisonLeft: {},
		searchTerm: '',
		recalculationSignal: 0,
	};
}

export function getNutrientsLeft(
	state: Type,
	input: {
		ingredientId: string;
		segmentIndex: number;
		calculation: Calculation.Type;
	}
): { [nutrientId: string]: number | null } {
	const defaultReturnValue = Calculation.mapNutrientsToKeyValueObject(
		input.calculation,
		input.ingredientId
	);
	const recipe = state.erpRecipes.find((e) => e.id === state.erpRecipeId);
	if (!recipe) return defaultReturnValue;
	const productId = state.comparisonLeft[input.ingredientId];
	if (!productId) return defaultReturnValue;
	const product = IngredientList.getProduct(recipe.ingredients, {
		ingredientId: input.ingredientId,
		segmentIndex: input.segmentIndex,
		productId: productId,
	});
	if (!product) return defaultReturnValue;
	return product.nutrients;
}

export function getNutrients(
	input: {
		ingredientId: string;
		calculation: Calculation.Type;
	},
	product?: Product.Type
): { [nutrientId: string]: number | null } {
	const defaultReturnValue = Calculation.mapNutrientsToKeyValueObject(
		input.calculation,
		input.ingredientId
	);
	if (!product) return defaultReturnValue;
	return product.nutrients;
}

export function getNutrientsRight(
	state: Type,
	input: {
		ingredientId: string;
		segmentIndex: number;
		calculation: Calculation.Type;
	}
): { [nutrientId: string]: number | null } {
	const defaultReturnValue = Calculation.mapNutrientsToKeyValueObject(
		input.calculation,
		input.ingredientId
	);
	const recipe = state.erpRecipes.find((e) => e.id === state.erpRecipeId);
	if (!recipe) return defaultReturnValue;
	const productId = state.comparisonRight[input.ingredientId];
	if (!productId) return defaultReturnValue;
	const product = IngredientList.getProduct(recipe.ingredients, {
		ingredientId: input.ingredientId,
		segmentIndex: input.segmentIndex,
		productId: productId,
	});
	if (!product) return defaultReturnValue;
	return product.nutrients;
}

export async function initialize(
	state: Type,
	input?: {
		recipeId: string | null;
		ingredients: IngredientList.Type;
	}
): Promise<Type> {
	if (!input) return state;
	if (!input.ingredients.length) return state;
	const recipes = await ErpRecipeList.initialize(input.recipeId, input.ingredients);
	return { ...state, erpRecipes: recipes };
}

export function toggleProductSelection(
	state: Type,
	input: { ingredientId: string; productId: string; segmentIndex: number }
): Type {
	return {
		...state,
		erpRecipes: ErpRecipeList.toggleProductSelection(
			state.erpRecipes,
			input.ingredientId,
			input.productId,
			input.segmentIndex
		),
		recalculationSignal: calculateRecalculationSignal(state.recalculationSignal),
	};
}

function calculateRecalculationSignal(input: number): number {
	return (input + 1) % 2;
}

export function setComparisonRight(
	state: Type,
	input: {
		ingredientId: string;
		productId: string | null;
	}
): Type {
	return {
		...state,
		comparisonRight: {
			...state.comparisonRight,
			[input.ingredientId]: input.productId,
		},
	};
}

export function setComparisonLeft(
	state: Type,
	input: {
		ingredientId: string;
		productId: string | null;
	}
): Type {
	return {
		...state,
		comparisonLeft: {
			...state.comparisonLeft,
			[input.ingredientId]: input.productId,
		},
	};
}

export function setErpRecipeId(state: Type, id: string | null): Type {
	return {
		...state,
		erpRecipeId: id,
	};
}

export function getErpRecipesBySearchTerm(state: Type): ErpRecipeList.Type {
	return state.erpRecipes.filter((e) => {
		if (state.searchTerm === '') return true;
		return e.name.toLowerCase().includes(state.searchTerm.toLowerCase());
	});
}

export function setSearchTerm(state: Type, input: string): Type {
	return {
		...state,
		searchTerm: input,
	};
}

export function navigateBackToOverview(state: Type): Type {
	return {
		...state,
		erpRecipeId: null,
		comparisonRight: {},
		comparisonLeft: {},
	};
}

export function setSelectedColumns(state: Type, ids: string[]): Type {
	return {
		...state,
		selectedColumns: ids,
	};
}

export function setValueScope(state: Type, valueScope: ValueScope): Type {
	return {
		...state,
		valueScope: valueScope,
	};
}

export async function createNewErpRecipe(state: Type): Promise<Type> {
	return {
		...state,
		erpRecipes: ErpRecipeList.makeNewErpRecipeFromTemplate(state.erpRecipes),
		erpRecipeId: ID.NEW,
	};
}

export function mapFirstSuggestionToProduct(state: Type): Type {
	return {
		...state,
		erpRecipes: ErpRecipeList.mapFirstSuggestionToProduct(state.erpRecipes, state.erpRecipeId),
		recalculationSignal: calculateRecalculationSignal(state.recalculationSignal),
	};
}

export function toggleComparison(
	state: Type,
	input: {
		ingredientId: string;
		productId: string;
		position: 'left' | 'right';
	}
) {
	const key = input.position === 'left' ? 'comparisonLeft' : 'comparisonRight';
	if (isInComparison(state, input)) {
		return {
			...state,
			[key]: {
				...state[key],
				[input.ingredientId]: null,
			},
		};
	}
	return {
		...state,
		[key]: {
			...state[key],
			[input.ingredientId]: input.productId,
		},
	};
}

export function isInComparison(
	state: Type,
	input: {
		ingredientId: string;
		productId: string;
		position: 'left' | 'right';
	}
): boolean {
	const key = input.position === 'left' ? 'comparisonLeft' : 'comparisonRight';
	if (state[key][input.ingredientId] === input.productId) return true;
	return false;
}

export function changeErpRecipeName(
	state: Type,
	input: {
		id: string;
		name: string;
	}
): Type {
	return {
		...state,
		erpRecipes: ErpRecipeList.changeName(state.erpRecipes, input.id, input.name),
	};
}

export async function saveErpRecipe(state: Type, id?: string): Promise<Type> {
	if (!id) return state;
	const erpRecipe = state.erpRecipes.find((e) => e.id === id);
	if (!erpRecipe) return state;
	const newId = await ErpRecipe.saveToApi(erpRecipe);
	return {
		...state,
		erpRecipes: ErpRecipeList.addNewId(state.erpRecipes, id, newId),
		erpRecipeId: newId,
	};
}

export async function deleteErpRecipe(state: Type, erpRecipeId?: string): Promise<Type> {
	if (!erpRecipeId) return state;
	return {
		...state,
		erpRecipes: await ErpRecipeList.deleteById(state.erpRecipes, erpRecipeId),
	};
}

export async function searchAndAddNewProductsToIngredient(
	state: Type,
	input?: {
		ingredientId: string;
		searchTerm: string;
		nrOfMatches: number;
	}
): Promise<Type> {
	if (!input) return state;
	if (!state.erpRecipeId) return state;
	if (input.searchTerm === '') {
		return resetManuallySearchedProductsFromIngredient(state, {
			ingredientId: input.ingredientId,
		});
	}
	return {
		...state,
		erpRecipes: await ErpRecipeList.searchAndAddToManuallySearchedProductsToIngredient(
			state.erpRecipes,
			state.erpRecipeId,
			input.ingredientId,
			input.searchTerm,
			input.nrOfMatches
		),
		recalculationSignal: calculateRecalculationSignal(state.recalculationSignal),
	};
}

export function resetManuallySearchedProductsFromIngredient(
	state: Type,
	input: { ingredientId: string }
): Type {
	if (!state.erpRecipeId) return state;
	return {
		...state,
		erpRecipes: ErpRecipeList.resetManuallySearchedProductsFromIngredient(
			state.erpRecipes,
			state.erpRecipeId,
			input.ingredientId
		),
		recalculationSignal: calculateRecalculationSignal(state.recalculationSignal),
	};
}

export function hasManuallySearchedProducts(
	state: Type,
	input?: { ingredientId: string }
): boolean {
	if (!input) return false;
	if (!state.erpRecipeId) return false;
	const erpRecipe = state.erpRecipes.find((e) => e.id === state.erpRecipeId);
	if (!erpRecipe) return false;
	const ingredient = erpRecipe.ingredients.find((e) => e.ingredientId === input.ingredientId);
	if (!ingredient) return false;
	return Boolean(ingredient.manuallySearchedProducts);
}
